import React, { useEffect, useState, useCallback } from "react";
import { CreditCard, Loader2, Star, Settings2, Save, PlugZap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS = {
  connected: { label: "Connected", cls: "bg-green-100 text-green-700" },
  disconnected: { label: "Not configured", cls: "bg-red-100 text-red-700" },
  auth_failed: { label: "Authentication Failed", cls: "bg-amber-100 text-amber-700" },
  pending: { label: "Pending Verification", cls: "bg-stone-100 text-stone-500" },
};

function PayPalConfig({ p, onSaved }) {
  const [env, setEnv] = useState(p.env || "sandbox");
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [currency, setCurrency] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const inp = "w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose/30";
  const save = async () => {
    setSaving(true);
    const body = { key: "paypal", env };
    if (clientId.trim()) body.client_id = clientId.trim();
    if (secret.trim()) body.secret = secret.trim();
    if (currency.trim()) body.currency = currency.trim();
    try { const r = await api.put("/admin/payment-providers", body); toast.success("PayPal settings saved"); setSecret(""); setClientId(""); onSaved(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/payment-providers/test");
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-stone-100" data-testid="paypal-config">
      <div className="flex items-center gap-2 mb-3 text-forest"><Settings2 size={15} className="text-rose" /><span className="text-sm font-semibold">PayPal credentials</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] text-stone-500">Environment</label>
          <select value={env} onChange={(e) => setEnv(e.target.value)} data-testid="paypal-env" className={inp}>
            <option value="sandbox">Sandbox (test)</option>
            <option value="live">Live (production)</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-stone-500">Currency</label>
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="paypal-currency" placeholder="USD" className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] text-stone-500">Client ID {p.client_id_masked && <span className="text-stone-400">(current: {p.client_id_masked})</span>}</label>
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="paypal-client-id" placeholder="Paste new Client ID to change" className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] text-stone-500">Secret {p.has_secret && <span className="text-green-700">(a secret is set)</span>}</label>
          <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="paypal-secret" placeholder="Paste new Secret to change" className={inp} autoComplete="new-password" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button onClick={save} disabled={saving} data-testid="paypal-save" className="btn-primary text-sm disabled:opacity-60">{saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save PayPal settings</>}</button>
        <button onClick={testConnection} disabled={testing} data-testid="paypal-test" className="text-sm px-4 py-2 rounded-full bg-sand text-forest font-semibold inline-flex items-center gap-2 disabled:opacity-60">{testing ? <Loader2 size={14} className="animate-spin" /> : <><PlugZap size={14} /> Test connection</>}</button>
      </div>
      <p className="text-[11px] text-stone-400 mt-1.5">Save your credentials first, then test — the check fetches a live PayPal auth token with the saved keys.</p>
    </div>
  );
}

export default function PaymentMethods() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/payment-providers").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const update = async (key, body, msg) => {
    setBusy(key);
    try { const r = await api.put("/admin/payment-providers", { key, ...body }); setRows(r.data); toast.success(msg); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="payment-methods">
      <div className="card-lux p-5">
        <div className="flex items-center gap-2 mb-1"><CreditCard size={18} className="text-rose" /><h3 className="font-serif text-xl text-forest">Payment providers</h3></div>
        <p className="text-xs text-stone-500">Enable or disable providers shown to guests at checkout and pick the default. PayPal environment and credentials can be updated below; the secret is encrypted and never shown again. Stripe keys remain in the server environment.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {rows.map((p) => {
          const st = STATUS[p.status] || STATUS.pending;
          return (
            <div key={p.key} className="card-lux p-5" data-testid={`provider-${p.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-serif text-lg text-forest">{p.provider}</span>
                  {p.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose/10 text-rose font-semibold inline-flex items-center gap-1"><Star size={11} /> Default</span>}
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`} data-testid={`provider-status-${p.key}`}>{st.label}</span>
              </div>
              {p.env && <div className="text-xs text-stone-400 mt-1">Environment: <span className={`font-semibold ${p.env === "live" ? "text-green-700" : "text-amber-600"}`}>{p.env}</span></div>}
              <div className="flex items-center gap-2 mt-4">
                <button onClick={() => update(p.key, { enabled: !p.enabled }, p.enabled ? `${p.provider} disabled` : `${p.provider} enabled`)} disabled={busy === p.key}
                  data-testid={`provider-toggle-${p.key}`}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold disabled:opacity-50 ${p.enabled ? "bg-forest text-cream" : "bg-stone-200 text-stone-600"}`}>
                  {busy === p.key ? <Loader2 size={13} className="animate-spin" /> : (p.enabled ? "Enabled — click to disable" : "Disabled — click to enable")}
                </button>
                {!p.is_default && (
                  <button onClick={() => update(p.key, { is_default: true }, `${p.provider} set as default`)} disabled={busy === p.key}
                    data-testid={`provider-default-${p.key}`} className="text-xs px-3 py-1.5 rounded-full bg-rose/10 text-rose font-semibold disabled:opacity-50">Set default</button>
                )}
              </div>
              {p.key === "paypal" && p.editable && <PayPalConfig p={p} onSaved={setRows} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
